import type { ReleaseCitySubdivisionSource } from './logistic-request-release-city.types';

export const LogistTimeManagementEntityType = {
  Request: 'request',
  Reservation: 'reservation',
} as const;

export type LogistTimeManagementEntityType =
  (typeof LogistTimeManagementEntityType)[keyof typeof LogistTimeManagementEntityType];

export const LogistTimeManagementReservationCreationType = {
  Manual: 'manual',
  Import: 'import',
} as const;

export type LogistTimeManagementReservationCreationType =
  (typeof LogistTimeManagementReservationCreationType)[keyof typeof LogistTimeManagementReservationCreationType];

export type LogistTimeManagementRequestEntityRef = {
  requestId: number;
  reservationId: null;
};

export type LogistTimeManagementReservationEntityRef = {
  requestId: null;
  reservationId: number;
};

export type LogistTimeManagementEntityRef =
  | LogistTimeManagementRequestEntityRef
  | LogistTimeManagementReservationEntityRef;

export interface LogistTimeManagementEntityRefLike {
  requestId: number | null;
  reservationId: number | null;
}

export function toLogistTimeManagementRequestEntityRef(
  requestId: number,
): LogistTimeManagementRequestEntityRef {
  return {
    requestId,
    reservationId: null,
  };
}

export function toLogistTimeManagementReservationEntityRef(
  reservationId: number,
): LogistTimeManagementReservationEntityRef {
  return {
    requestId: null,
    reservationId,
  };
}

export function isLogistTimeManagementRequestEntityRef(
  value: LogistTimeManagementEntityRefLike,
): value is LogistTimeManagementRequestEntityRef {
  return value.requestId !== null && value.reservationId === null;
}

export function isLogistTimeManagementReservationEntityRef(
  value: LogistTimeManagementEntityRefLike,
): value is LogistTimeManagementReservationEntityRef {
  return value.requestId === null && value.reservationId !== null;
}

export function getLogistTimeManagementEntityType(
  value: LogistTimeManagementEntityRefLike,
): LogistTimeManagementEntityType {
  if (isLogistTimeManagementRequestEntityRef(value)) {
    return LogistTimeManagementEntityType.Request;
  }

  if (isLogistTimeManagementReservationEntityRef(value)) {
    return LogistTimeManagementEntityType.Reservation;
  }

  throw new Error('Logist time management entity type is not set');
}

export function getLogistTimeManagementEntityId(
  value: LogistTimeManagementEntityRefLike,
): number {
  const entityId = value.requestId ?? value.reservationId;

  if (entityId !== null) return entityId;

  throw new Error('Logist time management entity id is not set');
}

export function getLogistTimeManagementEntityKey(
  value: LogistTimeManagementEntityRefLike,
): string {
  return `${getLogistTimeManagementEntityType(value)}:${getLogistTimeManagementEntityId(value)}`;
}

export enum LogistTimeManagementAuditEventName {
  EntityAssigned = 'entityAssigned',
  ReturnedToQueue = 'returnedToQueue',
  ProductsUpdated = 'productsUpdated',
  BulkApplied = 'bulkApplied',
  ChangeIntentionCreated = 'changeIntentionCreated',
  ChangeIntentionUpdated = 'changeIntentionUpdated',
  ChangeIntentionApplied = 'changeIntentionApplied',
  ChangeIntentionCanceled = 'changeIntentionCanceled',
  ReservationCreated = 'reservationCreated',
  ReservationDeleted = 'reservationDeleted',
  ReservationDateUpdated = 'reservationDateUpdated',
}

export enum LogistTimeManagementEditableField {
  DeliveryAddress = 'deliveryAddress',
  Date = 'date',
  Time = 'time',
  InstallZoneId = 'installZoneId',
  InstallerId = 'installerId',
  Products = 'products',
  ProductInstallers = 'productInstallers',
  LogistId = 'logistId',
  LogistCityId = 'logistCityId',
  /** @deprecated Data migration converts this field to LogistCityId. */
  CounterpartySubdivisionId = 'counterpartySubdivisionId',
}

export enum LogistTimeManagementRealtimeEventName {
  RegistryUpdated = 'logist-time-management.registry.updated',
}

export enum LogistTimeManagementRegistryUpdateReason {
  Assigned = 'assigned',
  BulkApplied = 'bulkApplied',
  ChangeIntentionCreated = 'changeIntentionCreated',
  ChangeIntentionUpdated = 'changeIntentionUpdated',
  ChangeIntentionApplied = 'changeIntentionApplied',
  ChangeIntentionCanceled = 'changeIntentionCanceled',
  SortUpdated = 'sortUpdated',
  ReturnedToQueue = 'returnedToQueue',
  ProductsUpdated = 'productsUpdated',
  ReservationCreated = 'reservationCreated',
  ReservationDeleted = 'reservationDeleted',
  ReservationMatched = 'reservationMatched',
  ReservationDateUpdated = 'reservationDateUpdated',
  RequestStatusUpdated = 'requestStatusUpdated',
  PersonalNoteUpdated = 'personalNoteUpdated',
}

export interface LogistTimeManagementDictionaryEntityDto {
  id: number;
  name: string;
}

export interface LogistTimeManagementPersonDto {
  id: number;
  name: string;
  avatar?: string | null;
}

export interface LogistTimeManagementRequestStatusDto {
  id: number;
  name: string | null;
  color: string | null;
}

export interface LogistTimeManagementInstallZoneDto {
  id: number;
  name: string;
  comment: string | null;
  price: string | null;
  changable: boolean | null;
}

export interface LogistTimeManagementInstallZoneCityDto {
  subdivisionId: number;
  name: string;
  contractorCounterpartyId: number;
  zones: LogistTimeManagementInstallZoneDto[];
}

export interface LogistTimeManagementInstallZoneSelectorDto {
  available: boolean;
  clientName: string | null;
  defaultSubdivisionId: number | null;
  cities: LogistTimeManagementInstallZoneCityDto[];
}

export interface LogistTimeManagementProductDto {
  id: number;
  productId: number;
  name: string;
  quantity?: number | null;
  deviceModel?: number | null;
  completed?: boolean | null;
  isClosedForcingly?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  installers?: LogistTimeManagementProductInstallerDto[];
}

export type LogistTimeManagementProductInstallerAction = 'upsert' | 'delete';

export interface LogistTimeManagementProductInstallerDto {
  id: number;
  productAssocId: number;
  productId: number;
  personId: number;
  person: LogistTimeManagementPersonDto | null;
  scheduledAt: string | null;
  amount: number | null;
  cost: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface LogistTimeManagementProductInstallerChangeDto {
  action: LogistTimeManagementProductInstallerAction;
  productId: number;
  personId: number;
  scheduledAt?: string | null;
  amount?: number | null;
  cost?: number | null;
}

export interface LogistTimeManagementProductInstallerIntentionRequestDto {
  entities: LogistTimeManagementIntentionVersionedEntityDto[];
  changes: LogistTimeManagementProductInstallerChangeDto[];
}

export interface LogistTimeManagementProductInstallerIntentionResponseDto {
  intentions: LogistTimeManagementChangeIntentionDto[];
}

export interface LogistTimeManagementReservationProductInputDto {
  productId: number;
  amount: number;
}

export interface LogistTimeManagementReservationCreateRequestDto {
  date?: string | null;
  time?: string | null;
  counterpartyId?: number | null;
  subjName?: string | null;
  deliveryName?: string | null;
  deliveryAddress?: string | null;
  contract?: string | null;
  projectNumber?: string | null;
  products?: LogistTimeManagementReservationProductInputDto[];
  logistId?: number | null;
  logistCityId?: number | null;
  /** @deprecated Use logistCityId. */
  counterpartySubdivisionId?: number | null;
  installerId?: number | null;
  installerSubdivisionId?: number | null;
}

export type LogistTimeManagementReservationCreateResponseDto =
  | {
      reservationId: number;
      destination: 'distribution';
      row: LogistTimeManagementDistributionRowDto | null;
    }
  | {
      reservationId: number;
      destination: 'registry';
      row: LogistTimeManagementRegistryRowDto | null;
    };

export interface LogistTimeManagementReservationDeleteRequestDto {
  reservationIds: number[];
  source?: 'overdue-management';
}

export interface LogistTimeManagementReservationDeleteResponseDto {
  deletedReservationIds: number[];
}

export type LogistTimeManagementOverdueReservationAssignedState =
  'all' | 'assigned' | 'unassigned';

export type LogistTimeManagementReservationDateUpdateTarget =
  'today' | 'tomorrow';

export interface LogistTimeManagementOverdueReservationsQueryDto {
  search?: string;
  releaseDateFrom?: string;
  releaseDateTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  counterpartyId?: number;
  assignedState?: LogistTimeManagementOverdueReservationAssignedState;
  limit?: number;
  offset?: number;
}

export interface LogistTimeManagementOverdueReservationDto {
  id: number;
  date: string;
  time: string | null;
  createdAt: string | null;
  counterparty: LogistTimeManagementDictionaryEntityDto | null;
  subjName: string | null;
  deliveryName: string | null;
  deliveryAddress: string | null;
  contract: string | null;
  projectNumber: string | null;
  logist: LogistTimeManagementPersonDto | null;
  subdivision: LogistTimeManagementDictionaryEntityDto | null;
  assigned: boolean;
  products: LogistTimeManagementProductDto[];
}

export interface LogistTimeManagementOverdueReservationsResponseDto {
  items: LogistTimeManagementOverdueReservationDto[];
  total: number;
  limit: number;
  offset: number;
}

export interface LogistTimeManagementReservationDateUpdateRequestDto {
  reservationIds: number[];
  target: LogistTimeManagementReservationDateUpdateTarget;
  dateTimeTransferReasonId: number;
}

export interface LogistTimeManagementReservationDateUpdateResponseDto {
  updatedReservationIds: number[];
  date: string;
}

export type LogistTimeManagementProductsEditableValue =
  | LogistTimeManagementRegistryProductUpdateDto[]
  | LogistTimeManagementProductDto[];

export type LogistTimeManagementEditableValue =
  | string
  | number
  | boolean
  | null
  | LogistTimeManagementProductsEditableValue
  | LogistTimeManagementProductInstallerChangeDto[];

export interface LogistTimeManagementEditableFieldValuesDto {
  deliveryAddress: string | null;
  date: string | null;
  time: string | null;
  installZoneId: number | null;
  installerId: number | null;
  logistId: number | null;
  logistCityId: number | null;
  /** @deprecated Use logistCityId. */
  counterpartySubdivisionId?: number | null;
  products: LogistTimeManagementRegistryProductUpdateDto[];
  extra: Record<string, unknown> | null;
}

export interface LogistTimeManagementEditableValuesDto {
  current: LogistTimeManagementEditableFieldValuesDto;
  effective: LogistTimeManagementEditableFieldValuesDto;
}

export interface LogistTimeManagementPersonalNoteDto {
  lastEditedByUserId: number;
  lastEditedByUserName: string;
  text: string;
  updatedAt: string;
}

export type LogistTimeManagementPersonalNoteUpdateRequestDto =
  LogistTimeManagementEntityRef & {
    text: string;
    expectedUpdatedAt?: string | null;
    force?: boolean;
  };

export type LogistTimeManagementPersonalNoteUpdateResponseDto =
  LogistTimeManagementEntityRef & {
    personalNote: LogistTimeManagementPersonalNoteDto | null;
  };

export type LogistTimeManagementExtraFieldName = `extra.${string}`;

export interface LogistTimeManagementChangeFieldDto {
  fieldName:
    LogistTimeManagementEditableField | LogistTimeManagementExtraFieldName;
  newValue: LogistTimeManagementEditableValue;
  /**
   * Подразделение, из которого выбран СЛС. Используется только для
   * InstallerId, чтобы назначить корректного исполнителя заявки.
   */
  installerSubdivisionId?: number | null;
}

/**
 * Поле mutation-запроса. expectedValue не сохраняется в change intention и
 * используется только для optimistic concurrency проверки.
 */
export interface LogistTimeManagementExpectedChangeFieldDto extends LogistTimeManagementChangeFieldDto {
  expectedValue?: LogistTimeManagementEditableValue;
}

export interface LogistTimeManagementChangedFieldDto extends LogistTimeManagementChangeFieldDto {
  oldValue: LogistTimeManagementEditableValue;
}

export type LogistTimeManagementDistributionRowDto =
  LogistTimeManagementEntityRefLike & {
    displayNumber: string | null;
    date: string;
    timeFrom: string | null;
    timeTo: string | null;
    address: string | null;
    deliveryAddress: string | null;
    releaseTime: string | null;
    subjName: string | null;
    deliveryName: string | null;
    ownerLogistFullname: string | null;
    ownerLogistPhone: string | null;
    supportManagerFullname: string | null;
    supportManagerPhone: string | null;
    contract: string | null;
    projectNumber: string | null;
    carType: string | null;
    carModel: string | null;
    counterparty: LogistTimeManagementDictionaryEntityDto | null;
    products: LogistTimeManagementProductDto[];
    requestStatus: LogistTimeManagementRequestStatusDto | null;
    logistId: number | null;
    logistCityId: number | null;
    releaseCitySubdivisionId: number | null;
    releaseCitySubdivisionSource: ReleaseCitySubdivisionSource | null;
    /** @deprecated Use logistCityId. */
    counterpartySubdivisionId?: number | null;
    creationType: LogistTimeManagementReservationCreationType | null;
    personalNote: LogistTimeManagementPersonalNoteDto | null;
    extra: Record<string, unknown> | null;
  };

export type LogistTimeManagementDistributionResponseDto =
  LogistTimeManagementDistributionRowDto[];

export interface LogistTimeManagementLogistDto {
  id: number;
  personId: number;
  person: LogistTimeManagementPersonDto;
  subdivisions: LogistTimeManagementLogistSubdivisionDto[];
}

export interface LogistTimeManagementLogistSubdivisionDto extends LogistTimeManagementDictionaryEntityDto {
  installers: LogistTimeManagementPersonDto[];
}

export type LogistTimeManagementLogistsResponseDto =
  LogistTimeManagementLogistDto[];

export type LogistTimeManagementChangeIntentionDto =
  LogistTimeManagementEntityRefLike & {
    id: number;
    fieldsToUpdate: LogistTimeManagementChangeFieldDto[];
    createdId: number | null;
    createdAt: string;
    updatedAt: string;
  };

export interface LogistTimeManagementChangeMarkerDto {
  fieldName:
    LogistTimeManagementEditableField | LogistTimeManagementExtraFieldName;
  oldValue: LogistTimeManagementEditableValue;
  newValue: LogistTimeManagementEditableValue;
  changedAt: string;
}

export interface LogistTimeManagementRegistryInstallerOptionDto extends LogistTimeManagementPersonDto {
  subdivisionId: number | null;
  subdivisionName: string | null;
  currentSubdivision: boolean;
}

export interface LogistTimeManagementRegistryLogistDto extends LogistTimeManagementPersonDto {
  subdivisions: LogistTimeManagementDictionaryEntityDto[];
}

export type LogistTimeManagementRegistryRowDto =
  LogistTimeManagementEntityRefLike & {
    displayNumber: string | null;
    date: string;
    timeFrom: string | null;
    timeTo: string | null;
    deliveryAddress: string | null;
    subjName: string | null;
    deliveryName: string | null;
    ownerLogistFullname: string | null;
    ownerLogistPhone: string | null;
    supportManagerFullname: string | null;
    supportManagerPhone: string | null;
    contract: string | null;
    projectNumber: string | null;
    carType: string | null;
    carModel: string | null;
    counterparty: LogistTimeManagementDictionaryEntityDto | null;
    products: LogistTimeManagementProductDto[];
    requestStatus: LogistTimeManagementRequestStatusDto | null;
    installZoneId: number | null;
    installZoneNameCashed: string | null;
    installPrice: string | null;
    releaseCitySubdivisionId: number | null;
    releaseCitySubdivisionSource: ReleaseCitySubdivisionSource | null;
    installZoneSelector: LogistTimeManagementInstallZoneSelectorDto;
    logist: LogistTimeManagementRegistryLogistDto | null;
    subdivision: LogistTimeManagementDictionaryEntityDto | null;
    installer: LogistTimeManagementPersonDto | null;
    editableValues: LogistTimeManagementEditableValuesDto;
    changeIntention: LogistTimeManagementChangeIntentionDto | null;
    changeMarkers: LogistTimeManagementChangeMarkerDto[];
    sortRank: string | null;
    creationType: LogistTimeManagementReservationCreationType | null;
    personalNote: LogistTimeManagementPersonalNoteDto | null;
    extra: Record<string, unknown> | null;
  };

export interface LogistTimeManagementRegistryQueryDto {
  dateFrom?: string;
  dateTo?: string;
  logistIds?: number[];
}

export type LogistTimeManagementRegistryResponseDto =
  LogistTimeManagementRegistryRowDto[];

export interface LogistTimeManagementEntitySyncRequestDto {
  entities: LogistTimeManagementAssignmentEntityDto[];
}

export interface LogistTimeManagementRegistrySyncResponseDto {
  rows: LogistTimeManagementRegistryRowDto[];
  removed: LogistTimeManagementAssignmentEntityDto[];
}

export interface LogistTimeManagementDistributionSyncResponseDto {
  rows: LogistTimeManagementDistributionRowDto[];
  removed: LogistTimeManagementAssignmentEntityDto[];
}

export type LogistTimeManagementRegistryInstallersQueryDto =
  LogistTimeManagementEntityRefLike;

export interface LogistTimeManagementRegistryInstallersResponseDto {
  installers: LogistTimeManagementRegistryInstallerOptionDto[];
}

export interface LogistTimeManagementRegistryRequestStatusChangeAdditionalDataDto {
  cancelReason?: string;
  requestToEditReason?: string;
}

export interface LogistTimeManagementRegistryRequestStatusChangeRequestDto {
  requestIds: number[];
  statusId: number;
  additionalData?: LogistTimeManagementRegistryRequestStatusChangeAdditionalDataDto;
}

export interface LogistTimeManagementRegistryRequestStatusPreviewRejectedDto {
  requestId: number;
  displayNumber: string;
  reason: string;
}

export interface LogistTimeManagementRegistryRequestStatusPreviewResponseDto {
  eligible: LogistTimeManagementRequestEntityRef[];
  rejected: LogistTimeManagementRegistryRequestStatusPreviewRejectedDto[];
}

export interface LogistTimeManagementRegistryRequestStatusApplyResponseDto {
  updated: LogistTimeManagementRequestEntityRef[];
  rejected: LogistTimeManagementRegistryRequestStatusPreviewRejectedDto[];
}

export type LogistTimeManagementChangeIntentionUpsertItemDto =
  LogistTimeManagementIntentionVersionedEntityDto & {
    fieldsToUpdate: LogistTimeManagementExpectedChangeFieldDto[];
  };

export interface LogistTimeManagementChangeIntentionUpsertRequestDto {
  items: LogistTimeManagementChangeIntentionUpsertItemDto[];
}

export interface LogistTimeManagementChangeIntentionUpsertResponseDto {
  intentions: LogistTimeManagementChangeIntentionDto[];
}

export interface LogistTimeManagementChangeIntentionBatchRequestDto {
  entities: LogistTimeManagementVersionedEntityDto[];
  dateTimeTransferReasonId?: number;
}

export interface LogistTimeManagementChangeIntentionApplyResponseDto {
  applied: LogistTimeManagementChangeIntentionDto;
}

export interface LogistTimeManagementChangeIntentionCancelResponseDto {
  canceled: LogistTimeManagementChangeIntentionDto;
}

export interface LogistTimeManagementChangeIntentionApplyBatchResponseDto {
  applied: LogistTimeManagementAssignmentEntityDto[];
}

export interface LogistTimeManagementChangeIntentionCancelBatchResponseDto {
  canceled: LogistTimeManagementAssignmentEntityDto[];
}

export type LogistTimeManagementRegistryBulkApplyItemDto =
  LogistTimeManagementScopedEntityDto & {
    fieldsToUpdate: LogistTimeManagementExpectedChangeFieldDto[];
  };

export interface LogistTimeManagementRegistryBulkApplyRequestDto {
  items: LogistTimeManagementRegistryBulkApplyItemDto[];
}

export interface LogistTimeManagementRegistryBulkApplyResponseDto {
  updated: LogistTimeManagementAssignmentEntityDto[];
}

export interface LogistTimeManagementRegistrySortTargetDto {
  scopeDate: string;
  logistId: number;
  logistCityId?: number | null;
  /** @deprecated Use logistCityId. */
  counterpartySubdivisionId?: number;
  scopeEntities: LogistTimeManagementAssignmentEntityDto[];
  expectedScopeEntities?: LogistTimeManagementAssignmentEntityDto[];
}

export interface LogistTimeManagementRegistrySortRequestDto {
  entities: LogistTimeManagementAssignmentEntityDto[];
  target: LogistTimeManagementRegistrySortTargetDto;
}

export type LogistTimeManagementRegistrySortItemDto =
  LogistTimeManagementEntityRefLike & {
    scopeDate: string;
    logistId: number;
    logistCityId?: number | null;
    sortRank: string;
  };

export interface LogistTimeManagementRegistrySortResponseDto {
  updated: LogistTimeManagementRegistrySortItemDto[];
  requiresReload: boolean;
}

export interface LogistTimeManagementReturnToQueueRequestDto {
  entities: LogistTimeManagementScopedEntityDto[];
}

export interface LogistTimeManagementReturnToQueueResponseDto {
  returned: LogistTimeManagementAssignmentEntityDto[];
}

export interface LogistTimeManagementRegistryProductUpdateDto {
  productId: number;
  quantity: number;
}

export interface LogistTimeManagementRegistryProductsUpdateRequestDto {
  entities: LogistTimeManagementScopedEntityDto[];
  products: LogistTimeManagementRegistryProductUpdateDto[];
}

export interface LogistTimeManagementRegistryProductsUpdateResponseDto {
  updated: LogistTimeManagementAssignmentEntityDto[];
}

export interface LogistTimeManagementRegistryRealtimeEventDto {
  eventName: LogistTimeManagementRealtimeEventName.RegistryUpdated;
  reason: LogistTimeManagementRegistryUpdateReason;
  schemaVersion?: 2;
  changes?: LogistTimeManagementRealtimeEntityChangeDto[];
  sourceClientId?: string | null;
  entity?: LogistTimeManagementAssignmentEntityDto;
  scopeDate?: string;
  logistIds?: number[];
  changedById: number | null;
  changedAt: string;
}

export type LogistTimeManagementAssignmentEntityDto =
  LogistTimeManagementEntityRefLike;

export type LogistTimeManagementRealtimeChangedField =
  | LogistTimeManagementEditableField
  | LogistTimeManagementExtraFieldName
  | 'entity'
  | 'sort'
  | 'requestStatus';

export type LogistTimeManagementRealtimeScopeDto =
  | {
      kind: 'registry';
      scopeDate: string;
      logistId: number;
      logistCityId?: number | null;
    }
  | {
      kind: 'distribution';
      scopeDate: string;
    };

export interface LogistTimeManagementRealtimeEntityChangeDto {
  entity: LogistTimeManagementAssignmentEntityDto;
  changedFields: LogistTimeManagementRealtimeChangedField[];
  beforeScope: LogistTimeManagementRealtimeScopeDto | null;
  afterScope: LogistTimeManagementRealtimeScopeDto | null;
}

export type LogistTimeManagementVersionedEntityDto =
  LogistTimeManagementScopedEntityDto & {
    expectedUpdatedAt?: string;
  };

/**
 * Версия pending change intention для optimistic-проверки при его изменении.
 * `null` означает, что на момент редактирования intention отсутствовал.
 */
export type LogistTimeManagementIntentionVersionedEntityDto =
  LogistTimeManagementScopedEntityDto & {
    expectedIntentionUpdatedAt?: string | null;
  };

export interface LogistTimeManagementChangeIntentionVersionRequestDto {
  expectedUpdatedAt?: string;
  expectedScope?: LogistTimeManagementRealtimeScopeDto | null;
  dateTimeTransferReasonId?: number;
}

export type LogistTimeManagementScopedEntityDto =
  LogistTimeManagementEntityRefLike & {
    expectedScope?: LogistTimeManagementRealtimeScopeDto | null;
  };

export interface LogistTimeManagementAssignmentTargetDto {
  personId: number;
  logistCityId: number | null;
  /** @deprecated Use logistCityId. */
  counterpartySubdivisionId?: number;
}

export interface LogistTimeManagementAssignmentRequestDto {
  entities: LogistTimeManagementScopedEntityDto[];
  target: LogistTimeManagementAssignmentTargetDto;
}

export interface LogistTimeManagementConflictDto {
  entity: LogistTimeManagementAssignmentEntityDto;
  field: string;
  expectedValue: unknown;
  currentValue: unknown;
}

export interface LogistTimeManagementConflictResponseDto {
  statusCode: 409;
  message: string;
  conflict: LogistTimeManagementConflictDto;
}

export type LogistTimeManagementAssignmentResultDto =
  LogistTimeManagementEntityRefLike & {
    logistId: number;
    logistCityId?: number | null;
    /** @deprecated Use logistCityId. */
    counterpartySubdivisionId?: number;
  };

export interface LogistTimeManagementAssignmentResponseDto {
  assigned: LogistTimeManagementAssignmentResultDto[];
}

export interface LogistTimeManagementLogsQueryDto {
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  cursor?: string;
  search?: string;
  eventNames?: LogistTimeManagementAuditEventName[];
  createdIds?: number[];
  requestId?: number;
  reservationId?: number;
  entityProjectNumber?: string;
  entityContract?: string;
  entityCounterpartyIds?: number[];
}

export interface LogistTimeManagementLogChangedFieldDto {
  fieldName:
    | LogistTimeManagementEditableField
    | LogistTimeManagementExtraFieldName
    | string;
  oldValue?: LogistTimeManagementEditableValue;
  newValue?: LogistTimeManagementEditableValue;
}

export interface LogistTimeManagementLogItemDto extends LogistTimeManagementEntityRefLike {
  id: number;
  eventName: LogistTimeManagementAuditEventName | string;
  createdAt: string;
  createdId: number | null;
  actor: LogistTimeManagementPersonDto | null;
  entity: {
    type: LogistTimeManagementEntityType;
    id: number;
    projectNumber: string | null;
    contract: string | null;
    counterpartyId: number | null;
  };
  changedFields: LogistTimeManagementLogChangedFieldDto[];
  contextSummary: string | null;
}

export interface LogistTimeManagementLogsResponseDto {
  items: LogistTimeManagementLogItemDto[];
  nextCursor: string | null;
}

export type LogistTimeManagementAuditContextValue =
  | string
  | number
  | boolean
  | null
  | LogistTimeManagementAuditContextValue[]
  | { [key: string]: LogistTimeManagementAuditContextValue };

export interface LogistTimeManagementAuditContextDto {
  old: {
    logistId: number | null;
    logistCityId: number | null;
    /** @deprecated Use logistCityId. */
    counterpartySubdivisionId?: number | null;
  };
  new: {
    logistId: number;
    logistCityId?: number | null;
    /** @deprecated Use logistCityId. */
    counterpartySubdivisionId?: number;
  };
  [key: string]: LogistTimeManagementAuditContextValue;
}

export interface LogistTimeManagementChangeIntentionCreatedAuditContextDto {
  fieldsToUpdate: LogistTimeManagementChangedFieldDto[];
  changeIntentionId: number;
  logistId: number | null;
}

export interface LogistTimeManagementChangeIntentionUpdatedAuditContextDto {
  fieldsToUpdate: LogistTimeManagementChangedFieldDto[];
  changeIntentionId: number;
  logistId: number | null;
}

export interface LogistTimeManagementChangeIntentionAppliedAuditContextDto {
  updatedFields: LogistTimeManagementChangeFieldDto[];
  changeIntentionId: number;
  logistId: number | null;
}

export interface LogistTimeManagementBulkAppliedAuditContextDto {
  updatedFields: LogistTimeManagementChangedFieldDto[];
  logistId: number | null;
}

export interface LogistTimeManagementChangeIntentionCanceledAuditContextDto {
  changeIntentionId: number;
  logistId: number | null;
}

export interface LogistTimeManagementReturnedToQueueAuditContextDto {
  previousLogistId: number | null;
  previousLogistCityId?: number | null;
  /** @deprecated Use previousLogistCityId. */
  previousSubdivisionId?: number | null;
}

export interface LogistTimeManagementRegistryProductsUpdatedAuditContextDto {
  products: LogistTimeManagementRegistryProductUpdateDto[];
  logistId: number | null;
}

export interface LogistTimeManagementReservationAuditContextDto {
  reservation: {
    id: number | null;
    date: string | null;
    time: string | null;
    counterpartyId: number | null;
    subjName: string | null;
    deliveryName: string | null;
    deliveryAddress: string | null;
    contract: string | null;
    projectNumber: string | null;
    logistId: number | null;
    logistCityId: number | null;
    /** @deprecated Use logistCityId. */
    counterpartySubdivisionId?: number | null;
    installerId?: number | null;
    installerSubdivisionId?: number | null;
  };
  products: LogistTimeManagementReservationProductInputDto[];
  source?: 'overdue-management';
}

export interface LogistTimeManagementReservationDateUpdatedAuditContextDto {
  reservation: LogistTimeManagementReservationAuditContextDto['reservation'];
  oldDate: string;
  newDate: string;
  source: 'overdue-management';
  products: LogistTimeManagementReservationProductInputDto[];
}

export interface LogistTimeManagementImportTemplateDto {
  name: string;
  slug: string;
  counterpartyId?: number | null;
  columns: LogistTimeManagementImportTemplateColumnDto[];
  supportsValuesImport: boolean;
}

export interface LogistTimeManagementImportTemplateColumnDto {
  parse: {
    type: 'date' | 'number' | 'string';
    assignTo: string;
  };
  label: string;
  required?: boolean;
}

export interface LogistTimeManagementImportTemplatesResponseDto {
  templates: LogistTimeManagementImportTemplateDto[];
}

export interface LogistTimeManagementImportFromFileSkippedRowFieldErrorDto {
  label: string;
  errorType: 'missing' | 'wrong_format';
}

export interface LogistTimeManagementImportFromFileSkippedRowDto {
  rowNumber: number;
  reason: string;
  fieldErrors?: LogistTimeManagementImportFromFileSkippedRowFieldErrorDto[];
  values?: Record<string, unknown>;
}

export interface LogistTimeManagementImportFromFileResultDto {
  sourceName: string;
  templateSlug: string;
  processedRows: number;
  createdCount: number;
  createdReservationIds: number[];
  intentionUpdatedReservationIds: number[];
  nonIntentionUpdatedReservationIds: number[];
  skippedRows: LogistTimeManagementImportFromFileSkippedRowDto[];
  validatedRows?: Record<string, unknown>[];
}
